import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Button, Flex, Heading, Icon, Spacer, Text, Tooltip } from '@percent/lemonade'
import { useNativeApply } from '@percent/cause-dashboard/common/components/nativeApply'
import { useAuthState, useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks'

import styles from './SavingsCalculator.module.scss'
import { SavingsPartner } from './savingsCalculator.config'
import { SAVINGS_CALCULATOR_STEPS, useSavingsCalculator } from './useSavingsCalculator'
import { PartnerSavingsResult, SavingsCalculatorSurface } from './SavingsCalculator.types'

const STEP_VIEW_EVENTS: Record<number, string> = {
  1: 'Savings calculator choose products step viewed',
  2: 'Savings calculator choose seats step viewed',
  3: 'Savings calculator summary step viewed'
}

type SavingsCalculatorStepsProps = {
  partners: SavingsPartner[]
  surface: SavingsCalculatorSurface
  source?: string
  onComplete: VoidFunction
}

export function SavingsCalculatorSteps({ partners, surface, source, onComplete }: SavingsCalculatorStepsProps) {
  const { t } = useTranslation()
  const { track } = useCausesPortalAnalytics()
  const { authState } = useAuthState()
  const { step, selection, results, totals, selectedCount, togglePartner, setSeats, formatAmount, goNext, goBack } =
    useSavingsCalculator({ partners })

  const analyticsContext = {
    source,
    surface,
    organisationId: authState.organisation?.id,
    accountId: authState.user?.id
  }

  useEffect(() => {
    const eventName = STEP_VIEW_EVENTS[step]

    if (!eventName) {
      return
    }

    track(eventName, analyticsContext)
  }, [step])

  const stepLabels = [
    t('savingsCalculator.steps.welcome'),
    t('savingsCalculator.steps.pickTools'),
    t('savingsCalculator.steps.seats'),
    t('savingsCalculator.steps.savings')
  ]

  const getNextLabel = (currentStep: number) => {
    switch (currentStep) {
      case 0:
        return t('savingsCalculator.actions.start')
      case 2:
        return t('savingsCalculator.actions.seeSavings')
      default:
        return t('savingsCalculator.actions.continue')
    }
  }

  return (
    <>
      <div className={styles.progress}>
        {stepLabels.map((label, index) => (
          <div key={label} className={styles.progressStep}>
            <span
              className={`${styles.progressDot} ${index <= step ? styles.progressDotActive : ''}`}
              data-testid={`savings-calculator-step-dot-${index}`}
            >
              {index + 1}
            </span>
            <span className={`${styles.progressLabel} ${index <= step ? styles.progressLabelActive : ''}`}>
              {label}
            </span>
            {index < SAVINGS_CALCULATOR_STEPS - 1 && <span className={styles.progressDivider} aria-hidden />}
          </div>
        ))}
      </div>

      <div className={`${styles.body} ${surface === 'modal' ? styles.bodyScroll : ''}`}>
        {step === 0 && <IntroStep />}

        {step === 1 && (
          <div>
            <Heading level="h4">{t('savingsCalculator.tools.heading')}</Heading>
            <Spacer size={2} axis="vertical" />
            <Text size="small" color="var(--colors-gray-600)">
              {t('savingsCalculator.tools.subheading')}
            </Text>

            <Spacer size={4} axis="vertical" />

            <div className={styles.toolsGrid}>
              {partners.map(partner => {
                const isSelected = !!selection[partner.id]

                return (
                  <button
                    key={partner.id}
                    type="button"
                    onClick={() => togglePartner(partner.id)}
                    className={`${styles.toolTile} ${isSelected ? styles.toolTileSelected : ''}`}
                    data-testid={`savings-calculator-tool-${partner.id}`}
                  >
                    <img src={partner.logo} alt={partner.displayName} className={styles.toolLogo} />
                    <div className={styles.toolMeta}>
                      <div>
                        <Text size="small" fontWeight="semiBold">
                          {partner.displayName}
                        </Text>
                      </div>
                      <div className={styles.toolDiscount}>
                        <Text size="small" color="var(--colors-purple-400)">
                          {t('savingsCalculator.tools.upTo', { percentage: partner.discountText })}
                        </Text>
                        {partner.tooltipText && (
                          <Tooltip content={partner.tooltipText}>
                            <span
                              className={styles.toolTooltipTrigger}
                              role="img"
                              aria-label={partner.tooltipText}
                              onClick={event => event.stopPropagation()}
                              onKeyUp={event => event.stopPropagation()}
                            >
                              <Icon name="info" size={4} color="gray300" />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <span className={`${styles.toolCheck} ${isSelected ? styles.toolCheckSelected : ''}`} aria-hidden>
                      {isSelected ? '✓' : ''}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <Heading level="h4">{t('savingsCalculator.seats.heading')}</Heading>
            <Spacer size={2} axis="vertical" />
            <Text size="small" color="var(--colors-gray-600)">
              {t('savingsCalculator.seats.subheading')}
            </Text>
            <Spacer size={4} axis="vertical" />
            <div className={styles.seatsList}>
              {Object.keys(selection).map(id => {
                const partner = partners.find(item => item.id === id)

                if (!partner) {
                  return null
                }

                const seats = selection[id].seats

                return (
                  <div key={id} className={styles.seatRow}>
                    <img src={partner.logo} alt={partner.displayName} className={styles.toolLogo} />
                    <div className={styles.seatMeta}>
                      <Text size="small" fontWeight="medium">
                        {partner.displayName}
                      </Text>
                    </div>
                    <div className={styles.seatStepper}>
                      <button
                        type="button"
                        className={styles.stepperButton}
                        onClick={() => setSeats(id, seats - 1)}
                        aria-label={t('savingsCalculator.seats.decrease', { name: partner.displayName })}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={10000}
                        value={seats}
                        onChange={event => setSeats(id, parseInt(event.target.value, 10) || 1)}
                        className={styles.seatInput}
                        aria-label={t('savingsCalculator.seats.seatsFor', { name: partner.displayName })}
                      />
                      <button
                        type="button"
                        className={styles.stepperButton}
                        onClick={() => setSeats(id, seats + 1)}
                        aria-label={t('savingsCalculator.seats.increase', { name: partner.displayName })}
                      >
                        +
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <ResultsStep
            results={results}
            formatAmount={formatAmount}
            savingLabel={formatAmount(totals.saving)}
            currentLabel={formatAmount(totals.current)}
            afterLabel={formatAmount(totals.after)}
            onApplyClick={partner =>
              track(`Savings calculator apply clicked - ${partner.displayName}`, {
                ...analyticsContext,
                partnerId: partner.id,
                partnerName: partner.displayName,
                smartApply: !!partner.smartApply
              })
            }
            onClaim={() => {
              track('Savings calculator explore products clicked', {
                ...analyticsContext,
                estimatedAnnualSaving: totals.saving
              })
              onComplete()
            }}
          />
        )}
      </div>

      <div
        className={`${styles.footer} ${step === 0 ? styles.footerEnd : ''} ${
          surface === 'modal' ? styles.footerSticky : ''
        }`}
      >
        {step > 0 && (
          <Button variant="secondary" onClick={goBack}>
            {t('savingsCalculator.actions.back')}
          </Button>
        )}
        {step < SAVINGS_CALCULATOR_STEPS - 1 && (
          <Button variant="primary" onClick={goNext} disabled={step === 1 && selectedCount === 0}>
            {getNextLabel(step)}
          </Button>
        )}
      </div>
    </>
  )
}

function IntroStep() {
  const { t } = useTranslation()

  return (
    <div className={styles.intro}>
      <div className={styles.introMark} aria-hidden>
        ✨
      </div>
      <Heading level="h3" align="center">
        {t('savingsCalculator.intro.heading')}
      </Heading>
      <div className={styles.introCopy}>
        <Spacer size={2} axis="vertical" />
        <Text size="small" align="center" color="var(--colors-gray-600)">
          {t('savingsCalculator.intro.body')}
        </Text>
      </div>
    </div>
  )
}

type ResultsStepProps = {
  results: PartnerSavingsResult[]
  formatAmount: (amount: number) => string
  savingLabel: string
  currentLabel: string
  afterLabel: string
  onApplyClick: (partner: SavingsPartner) => void
  onClaim: VoidFunction
}

function ResultsStep({
  results,
  formatAmount,
  savingLabel,
  currentLabel,
  afterLabel,
  onApplyClick,
  onClaim
}: ResultsStepProps) {
  const { t } = useTranslation()
  const { openNativeApply } = useNativeApply()

  const handleApply = (partner: SavingsPartner) => {
    onApplyClick(partner)

    if (partner.smartApply) {
      openNativeApply({
        partnerKey: partner.smartApply.partnerKey,
        modalContext: partner.smartApply.modalContext,
        tracking: { programmeOfferName: partner.displayName, source: 'savings-calculator-results' }
      })

      return
    }

    if (partner.partnerUrl) {
      window.open(partner.partnerUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div>
      <div className={styles.resultsHero}>
        <Text size="small" color="var(--colors-white)">
          {t('savingsCalculator.results.eyebrow')}
        </Text>
        <div className={styles.resultsAmount}>{savingLabel}</div>
        <Text size="small" color="var(--colors-white)">
          {t('savingsCalculator.results.perYear', { count: results.length })}
        </Text>
        <div className={styles.resultsStats}>
          <ResultsStat label={t('savingsCalculator.results.current')} value={currentLabel} />
          <ResultsStat label={t('savingsCalculator.results.withGoodstack')} value={afterLabel} />
          <ResultsStat label={t('savingsCalculator.results.saved')} value={savingLabel} />
        </div>
      </div>

      <div className={styles.resultsTableWrapper}>
        <table className={styles.resultsTable}>
          <thead className={styles.resultsTableHead}>
            <tr>
              <th className={styles.resultsTableHeadCell}>{t('savingsCalculator.results.table.tool')}</th>
              <th className={styles.resultsTableHeadCell}>{t('savingsCalculator.results.table.now')}</th>
              <th className={styles.resultsTableHeadCell}>{t('savingsCalculator.results.table.withGoodstack')}</th>
              <th className={styles.resultsTableHeadCell}>{t('savingsCalculator.results.table.save')}</th>
              <th className={styles.resultsTableHeadCell} aria-hidden />
            </tr>
          </thead>
          <tbody className={styles.resultsTableBody}>
            {results.map(({ partner, current, after, saving }) => (
              <tr key={partner.id} className={styles.resultsTableRow}>
                <td className={`${styles.resultsTableCell} ${styles.resultsTableToolCell}`}>
                  <div className={styles.resultsTableTool} title={partner.tooltipText}>
                    <img src={partner.logo} alt={partner.displayName} className={styles.resultsTableLogo} />
                    <Text size="small" fontWeight="medium">
                      {partner.displayName}
                    </Text>
                  </div>
                </td>
                <td
                  className={`${styles.resultsTableCell} ${styles.resultsNow}`}
                  data-label={t('savingsCalculator.results.table.now')}
                >
                  {formatAmount(current)}
                </td>
                <td className={styles.resultsTableCell} data-label={t('savingsCalculator.results.table.withGoodstack')}>
                  {formatAmount(after)}
                </td>
                <td
                  className={`${styles.resultsTableCell} ${styles.resultsSaving}`}
                  data-label={t('savingsCalculator.results.table.save')}
                >
                  {formatAmount(saving)}
                </td>
                <td className={`${styles.resultsTableCell} ${styles.resultsApplyCell}`}>
                  {(partner.smartApply || partner.partnerUrl) && (
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => handleApply(partner)}
                      data-testid={`savings-calculator-apply-${partner.id}`}
                    >
                      {t('button.apply')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Flex direction="column" className={styles.resultsCta}>
        <Badge variant="positive">{t('savingsCalculator.results.badge', { amount: savingLabel })}</Badge>
        <Spacer size={3} axis="vertical" />
        <Button variant="primary" stretch onClick={onClaim}>
          {t('savingsCalculator.results.cta')}
        </Button>
      </Flex>
    </div>
  )
}

function ResultsStat({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.resultsStat}>
      <div>
        <Text size="small" color="var(--colors-white)">
          {label}
        </Text>
      </div>
      <Text fontWeight="semiBold" color="var(--colors-white)">
        {value}
      </Text>
    </div>
  )
}
